import { useEffect, useState } from "react";
import { Avatar, IconButton } from "@mui/material";
import moment from "moment";
import { toast } from "@/utils/appToast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { api } from "@/utils/api";
import { getBackendCallErrorMessage } from "@/utils/backendErrorMessage";
import { Spinner } from "@/shared/spinner/spinner";
import { ChatBlackIcon } from "@/components/assets/icons/svgs";

type RequestQuoteProposalsViewProps = {
  requestQuoteJobId: string;
  onAccepted: (job: any, acceptedProposal: any) => void;
  onClose?: () => void;
};

const statusBadgeClass = (status: string) => {
  if (status === "submitted")
    return "bg-[#e7eeff] text-[#4285F4] border-[#d8e3fb]";
  if (status === "accepted")
    return "bg-green-50 text-green-700 border-green-200";
  if (status === "rejected") return "bg-red-50 text-red-700 border-red-200";
  return "bg-[#f9f9ff] text-[#424654] border-[#d8e3fb]";
};

const unwrapProposalsResponse = (res: any) => {
  if (!res) return null;
  const body = res.success !== undefined ? res.data : res;
  if (!body || typeof body !== "object") return null;
  if (Array.isArray(body.proposals)) return body;
  if (body.data && Array.isArray(body.data.proposals)) return body.data;
  return body;
};

export default function RequestQuoteProposalsView({
  requestQuoteJobId,
  onAccepted,
}: RequestQuoteProposalsViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state: { user: any }) => state.user);
  const [loading, setLoading] = useState(true);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [proposals, setProposals] = useState<any[]>([]);
  const [isClient, setIsClient] = useState(true);
  const [quoteConverted, setQuoteConverted] = useState(false);

  const loadProposals = async () => {
    if (!requestQuoteJobId) return;
    setLoading(true);
    try {
      const res = await api.getProposalsForRequestQuote(requestQuoteJobId);
      if (res?.success === false) {
        toast.error(
          getBackendCallErrorMessage(res) ||
            res?.message ||
            t("something_went_wrong")
        );
        setProposals([]);
        return;
      }

      const data = unwrapProposalsResponse(res);
      if (!data) {
        toast.error(t("something_went_wrong"));
        setProposals([]);
        return;
      }

      if (data.isClient === false) {
        setIsClient(false);
        toast.error(t("unauthorizedProposals") || "Unauthorized");
        setProposals([]);
        return;
      }

      setIsClient(true);
      const list = Array.isArray(data.proposals) ? data.proposals : [];
      setProposals(list);

      setQuoteConverted(
        list.some((p) => p?.status === "accepted" || p?.convertedJobId != null)
      );
    } catch (err: any) {
      toast.error(err?.message || t("something_went_wrong"));
      setProposals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadProposals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestQuoteJobId]);

  const handleChatNavigation = (provider: any) => {
    const providerId = provider?._id;
    const providerProfileId =
      provider?.workerProfile?._id || provider?.workerProfile || null;
    if (!user?._id || !providerId) {
      toast.error(
        t("something_went_wrong") || "Could not open chat with this provider."
      );
      return;
    }
    navigate("/chat", {
      state: {
        clientId: user._id,
        providerId,
        providerProfileId,
        returnTo: `/request-quotes/${requestQuoteJobId}`,
      },
    });
  };

  const handleAccept = async (proposalId: string) => {
    if (!proposalId || acceptingId) return;
    setAcceptingId(proposalId);
    try {
      const res = await api.acceptRequestQuoteProposal(proposalId);
      if (res?.success === false) {
        toast.error(
          getBackendCallErrorMessage(res) ||
            res?.message ||
            t("something_went_wrong")
        );
        if (
          String(res?.message || "")
            .toLowerCase()
            .includes("already")
        ) {
          setQuoteConverted(true);
          await loadProposals();
        }
        return;
      }

      const body = res?.data?.job ? res.data : res?.data?.data || res?.data || res;
      const job = body?.job;
      const acceptedProposal = body?.acceptedProposal;

      if (!job) {
        toast.error(
          getBackendCallErrorMessage(res) || t("something_went_wrong")
        );
        await loadProposals();
        return;
      }

      toast.success(t("proposal_accepted"));
      setQuoteConverted(true);
      onAccepted(job, acceptedProposal);
    } catch (err: any) {
      toast.error(err?.message || t("something_went_wrong"));
    } finally {
      setAcceptingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner isLoading size="medium" />
      </div>
    );
  }

  if (!isClient) {
    return (
      <div className="bg-white rounded-xl border border-[#d8e3fb] px-6 py-10 text-center">
        <p className="text-sm text-[#424654]">
          {t("unauthorizedProposals") ||
            "You are not authorized to view these proposals."}
        </p>
      </div>
    );
  }

  if (proposals.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-[#d8e3fb] px-6 py-10 text-center">
        <p className="text-sm text-[#424654]">{t("noProposalsYet")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {quoteConverted && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {t("quoteConvertedToJob")}
        </div>
      )}

      {proposals.map((proposal) => {
        const provider = proposal?.providerId || {};
        const name =
          `${provider.first_name || ""} ${provider.last_name || ""}`.trim() ||
          t("professionals");
        const status = proposal?.status || "submitted";
        const canAccept = status === "submitted" && !quoteConverted;
        const isHourly =
          proposal?.paymentType === "hourly" ||
          (proposal?.hourlyRate != null && proposal?.totalHours != null);
        const initials = name
          .split(" ")
          .map((part: string) => part.charAt(0))
          .join("")
          .slice(0, 2)
          .toUpperCase();

        return (
          <div
            key={proposal._id}
            className="rounded-xl border border-[#d8e3fb] bg-white p-4 shadow-sm flex flex-col gap-3 min-w-0 overflow-hidden"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {provider.avatar ? (
                  <Avatar
                    src={provider.avatar}
                    alt={name}
                    sx={{ width: 56, height: 56 }}
                  />
                ) : (
                  <div className="size-14 rounded-full bg-[#d8e3fb] flex items-center justify-center text-sm font-semibold text-[#4285F4]">
                    {initials}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-semibold text-[#111c2d] truncate">
                    {name}
                  </p>
                  <p className="text-sm text-[#424654] mt-0.5">
                    <span className="font-semibold text-[#111c2d]">
                      ${Number(proposal.budget || 0).toFixed(2)}
                    </span>
                    {isHourly
                      ? ` (${proposal.hourlyRate}/hr × ${proposal.totalHours}h)`
                      : ""}
                  </p>
                </div>
              </div>
              <span
                className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold capitalize ${statusBadgeClass(
                  status
                )}`}
              >
                {t(status) !== status ? t(status) : status}
              </span>
            </div>

            {proposal.description && (
              <p className="text-sm text-[#424654] break-words whitespace-pre-wrap [overflow-wrap:anywhere] max-w-full min-w-0">
                {proposal.description}
              </p>
            )}

            <div className="flex flex-col gap-1 text-xs text-[#424654] min-w-0">
              {proposal.schedule?.startDateTime && (
                <p className="break-words [overflow-wrap:anywhere]">
                  {t("schedule") || "Schedule"}:{" "}
                  {moment(proposal.schedule.startDateTime).format(
                    "DD MMM YYYY, HH:mm"
                  )}
                  {proposal.schedule?.endDateTime
                    ? ` – ${moment(proposal.schedule.endDateTime).format(
                        "HH:mm"
                      )}`
                    : ""}
                </p>
              )}
              {proposal.location?.value && (
                <p className="break-words [overflow-wrap:anywhere]">
                  {t("location")}: {proposal.location.value}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#e7eeff]">
              <IconButton
                size="small"
                onClick={() => handleChatNavigation(provider)}
                aria-label={t("openChat") || "Open chat"}
                title={t("openChat") || "Open chat"}
              >
                <ChatBlackIcon />
              </IconButton>
              <button
                type="button"
                disabled={!canAccept || !!acceptingId}
                onClick={() => void handleAccept(proposal._id)}
                className={`h-10 px-6 rounded-lg text-sm font-semibold transition-colors ${
                  canAccept
                    ? "bg-[#4285F4] text-white shadow-sm hover:bg-[#4285F4]"
                    : "bg-[#dce3f0] text-[#5e6570] cursor-not-allowed"
                }`}
              >
                {acceptingId === proposal._id
                  ? t("submitting")
                  : t("accept")}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
